import { useFormikContext } from 'formik';
import { useRef, useState } from 'react';

import { Callout } from '../cdl/Callout/Callout';
import { translate } from '../common/helpers/translate.helper';
import { Box } from '../common/ui/Box';

import { FormikDefaultContractSelect } from './FormikDefaultContractSelect';
import { FormikVesselDefaultSupplierSelect } from './FormikVesselDefaultSupplierSelect';
import { useDefaultContractOptions } from './useDefaultContractOptions';

interface FormikVesselDefaultSupplierFlowProps {
    customerId?: string;
    vesselId?: string;
}

interface VesselDefaultSelections {
    defaultSupplierId: string;
    defaultContractId: string | null;
}

export const FormikVesselDefaultSupplierFlow = ({ customerId, vesselId }: FormikVesselDefaultSupplierFlowProps) => {
    const { setFieldValue, values } = useFormikContext<VesselDefaultSelections>();
    const { fetchFirstOptionsPage } = useDefaultContractOptions({ customerId, vesselId });
    const pendingSupplierId = useRef<string | undefined>(undefined);
    const [autoSelectedContractId, setAutoSelectedContractId] = useState<string>();

    const handleSupplierChange = async (supplierId: string) => {
        pendingSupplierId.current = supplierId;
        setAutoSelectedContractId(undefined);
        await setFieldValue('defaultContractId', null, false);

        if (!customerId || !supplierId) {
            pendingSupplierId.current = undefined;
            return;
        }

        const contracts = await fetchFirstOptionsPage(supplierId).catch(() => undefined);
        if (!contracts || pendingSupplierId.current !== supplierId) {
            return;
        }

        pendingSupplierId.current = undefined;
        const firstContractPage = contracts.pages[0];
        if (firstContractPage.total !== 1) {
            return;
        }

        const soleContract = firstContractPage.content[0];
        await setFieldValue('defaultContractId', soleContract.value);
        setAutoSelectedContractId(soleContract.value);
    };

    return (
        <>
            <FormikVesselDefaultSupplierSelect
                name="defaultSupplierId"
                customerIds={customerId ? [customerId] : []}
                label={translate('vessel.form.defaultSupplier')}
                disabled={!customerId}
                markAsOptional
                isClearable
                onChange={handleSupplierChange}
            />
            {customerId && values.defaultSupplierId ? (
                <Box display="grid" gap={8}>
                    <FormikDefaultContractSelect
                        name="defaultContractId"
                        defaultSupplierId={values.defaultSupplierId}
                        customerId={customerId}
                        vesselId={vesselId}
                        markAsOptional
                        onChange={() => setAutoSelectedContractId(undefined)}
                    />
                    {autoSelectedContractId === values.defaultContractId ? (
                        <Callout description={translate('vessel.form.defaultContractAutoSelected')} />
                    ) : null}
                </Box>
            ) : null}
        </>
    );
};
